// BathroomPreview.js
import React, { useRef, useEffect } from 'react';
import {
  Engine,
  Scene,
  ArcRotateCamera,
  Vector3,
  PBRMaterial,
  Texture,
  HemisphericLight,
  PointLight,
  Color3,
  Color4,
  VertexBuffer,
  MeshBuilder,
  Plane,              // <-- import Plane
} from '@babylonjs/core';

// For .gltf/.glb loading
import '@babylonjs/loaders';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';

// Import MirrorTexture (note: depending on your setup, you may need a direct path)
import { MirrorTexture } from '@babylonjs/core/Materials/Textures/mirrorTexture';

const BathroomPreview = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    // 1) Create engine/scene
    const canvas = canvasRef.current;
    if (!canvas) {
      console.warn('No canvas reference');
      return;
    }
    const engine = new Engine(canvas, true);
    const scene = new Scene(engine);

    // Scene background
    scene.clearColor = new Color4(0.8, 0.8, 0.8, 1);

    // 2) Camera
    const camera = new ArcRotateCamera(
      'camera',
      Math.PI / 4,
      Math.PI / 3,
      6,
      Vector3.Zero(),
      scene
    );
    camera.wheelPrecision = 50;
    camera.attachControl(canvas, true);

    // 3) Lights
    const downlightPositions = [
      new Vector3(0.5, 1, 0.5),
      new Vector3(0.5, 1, -0.5),
      new Vector3(-0.5, 1, 0.5),
      new Vector3(-0.5, 1, -0.5),
    ];
    downlightPositions.forEach((pos, i) => {
      const pointLight = new PointLight(`pointLight${i}`, pos, scene);
      pointLight.intensity = 2;
      pointLight.range = 2;
      pointLight.diffuse = new Color3(1.0, 0.95, 0.9);
    });
    const hemi = new HemisphericLight('hemi', new Vector3(0, 1, 0), scene);
    hemi.intensity = 0.2;

    // 4) Bathroom tile material
    const bathroomMat = new PBRMaterial('whiteCeramicPBR', scene);
    bathroomMat.albedoTexture = new Texture('/textures/walls/TilesMosaicYubi003_COL_8K.png', scene);
    bathroomMat.bumpTexture = new Texture('/textures/walls/TilesMosaicYubi003_NRM_8K.png', scene);
    bathroomMat.reflectivityTexture = new Texture('/textures/walls/TilesMosaicYubi003_REFL_8K.png', scene);
    bathroomMat.specularGlossinessTexture = new Texture('/textures/walls/TilesMosaicYubi003_GLOSS_8K.png', scene);
    bathroomMat.ambientTexture = new Texture('/textures/walls/TilesMosaicYubi003_AO_8K.png', scene);

    bathroomMat.useSpecularOverAlpha = true;
    bathroomMat.useAlphaFromAlbedoTexture = false;
    bathroomMat.roughness = 0.1;
    bathroomMat.metallic = 0.0;
    // Optional parallax
    bathroomMat.useParallaxOcclusion = true;
    bathroomMat.parallaxScaleBias = 1;

    // 5) Load your bathroom model
    const modelUrl =
      'https://yuxbcsdfnhttlrmzsqvz.supabase.co/storage/v1/object/public/products/bathroom.glb';

    SceneLoader.ImportMesh('', modelUrl, '', scene, (meshes) => {
      meshes.forEach((mesh) => {
        if (!mesh.isVerticesDataPresent(VertexBuffer.UVKind)) {
          assignBoxUVsRealWorld(mesh, 0.5);
        }
        mesh.material = bathroomMat;
      });
    });

    // 6) Create a real-time mirror using MirrorTexture
    const mirrorPlane = MeshBuilder.CreatePlane(
      'mirrorPlane',
      { width: 0.5, height: 0.5 },
      scene
    );
    mirrorPlane.position = new Vector3(0, 0, 0);

    // MirrorTexture (renders the scene from the mirror's perspective)
    const mirrorTexture = new MirrorTexture('mirrorTex', 1024, scene, true);
    // Adjust plane if needed (assuming the mirror faces +Z):
    mirrorTexture.mirrorPlane = new Plane(0, 0, 1, 0); 
    // If your mirror is flipped, try new Plane(0, 0, -1, 0)

    // Create a PBR material that uses the mirrorTexture as reflection
    const mirrorMat = new PBRMaterial('mirrorMat', scene);
    mirrorMat.metallic = 1.0;      // fully metallic
    mirrorMat.roughness = 0.0;     // zero roughness => perfect mirror
    mirrorMat.backFaceCulling = false; 
    mirrorMat.reflectionTexture = mirrorTexture;
    // Optional: tweak the reflection intensity
    mirrorMat.reflectionTexture.level = 1.0;

    mirrorPlane.material = mirrorMat;

    // We usually exclude the mirror from reflecting itself
    // (and optionally exclude the frame pieces if you want).
    scene.onBeforeRenderObservable.add(() => {
      const allButMirror = scene.meshes.filter((m) => m !== mirrorPlane);
      mirrorTexture.renderList = allButMirror;
    });

    // 7) Create a 5cm border frame around the mirror
    const frameMat = new PBRMaterial('frameMat', scene);
    frameMat.albedoColor = new Color3(0.05, 0.05, 0.05);  
    frameMat.metallic = 1.0;
    frameMat.roughness = 0.3;

    const totalSize = 0.6;   // outer dimension
    const borderThickness = 0.05;
    const depth = 0.01;

    // top bar
    const topBar = MeshBuilder.CreateBox('topBar', {
      width: totalSize,
      height: borderThickness,
      depth
    }, scene);
    topBar.material = frameMat;
    topBar.position = new Vector3(0, 0.25 + borderThickness / 2, 0);

    // bottom bar
    const bottomBar = topBar.createInstance('bottomBar');
    bottomBar.position = new Vector3(0, -0.25 - borderThickness / 2, 0);

    // left bar
    const leftBar = MeshBuilder.CreateBox('leftBar', {
      width: borderThickness,
      height: 0.5,
      depth
    }, scene);
    leftBar.material = frameMat;
    leftBar.position = new Vector3(-0.25 - borderThickness / 2, 0, 0);

    // right bar
    const rightBar = leftBar.createInstance('rightBar');
    rightBar.position = new Vector3(0.25 + borderThickness / 2, 0, 0);

    // 8) Render loop
    engine.runRenderLoop(() => {
      scene.render();
    });

    // Cleanup
    return () => {
      scene.dispose();
      engine.dispose();
    };
  }, []);

  // Helper: apply basic “box mapping” in real-world scale
  function assignBoxUVsRealWorld(mesh, tileSize = 0.5) {
    const positions = mesh.getVerticesData(VertexBuffer.PositionKind);
    if (!positions) return;

    let normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    if (!normals) {
      mesh.forceSharedVertices();
      mesh.computeNormals(true);
      normals = mesh.getVerticesData(VertexBuffer.NormalKind);
    }

    const uv = [];
    for (let i = 0; i < positions.length; i += 3) {
      const px = positions[i];
      const py = positions[i + 1];
      const pz = positions[i + 2];
      const nx = normals[i];
      const ny = normals[i + 1];
      const nz = normals[i + 2];

      const absNx = Math.abs(nx);
      const absNy = Math.abs(ny);
      const absNz = Math.abs(nz);

      let u = 0, v = 0;
      if (absNx >= absNy && absNx >= absNz) {
        // ±X face => (z,y)
        u = pz / tileSize;
        v = py / tileSize;
      } else if (absNy >= absNx && absNy >= absNz) {
        // ±Y face => (x,z)
        u = px / tileSize;
        v = pz / tileSize;
      } else {
        // ±Z face => (x,y)
        u = px / tileSize;
        v = py / tileSize;
      }
      uv.push(u, v);
    }

    mesh.setVerticesData(VertexBuffer.UVKind, uv, true);
    console.log(`Assigned box UVs for mesh ${mesh.name}, tileSize=${tileSize}.`);
  }

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '900px', display: 'block' }}
    />
  );
};

export default BathroomPreview;
